import { TranslatePipe } from "@ngx-translate/core";
import { AngularSvgIconModule } from "angular-svg-icon";

import { ChangeDetectionStrategy, Component, EventEmitter, computed, inject, input, linkedSignal } from "@angular/core";
import { FormsModule } from "@angular/forms";

import { SelfServiceCommandKeys } from "../../../helpers/enums/selfServiceCommands";
import { GuidPipe } from "../../../helpers/guidpipe/guidpipe";
import { StartLoadMessage } from "../../../helpers/self-service/models/types";
import { ModifyLightPath, ModifyRedundantLightPath } from "../../../helpers/self-service/modify_lightpath";
import { SelfServiceCommand } from "../../../helpers/self-service/self-service-command";
import { isValidVlan } from "../../../helpers/self-service/vlanhelper";
import { LightPathBothSubscriptionDetails } from "../../../pages/subscription-detail/types";
import { AuthService } from "../../../services/authservice";
import { SelfserviceWizard } from "../selfservice-wizard/selfservice-wizard";

@Component({
  selector: "selfservice-vlan-lightpath",
  templateUrl: "selfservice-vlan-lightpath.html",
  styleUrl: "../../selfservice/selfservice-add-port/selfservice-add-port.scss",
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [AngularSvgIconModule, FormsModule, GuidPipe, SelfserviceWizard, TranslatePipe],
})
export class SelfserviceVlanLightpath {
  private auth = inject(AuthService);

  protected subscription = input.required<LightPathBothSubscriptionDetails>();
  protected bus = input.required<EventEmitter<StartLoadMessage>>();

  private circuits = computed(() => this.subscription().circuits);
  protected isRedundant = computed(() => this.subscription().product.tag === "LR");

  protected endpoints = linkedSignal(() =>
    this.circuits()[0].endpoints.map((endpoint) => {
      const usedVlans = (endpoint.port?.usedVlans ?? []).filter((v) => v !== null && v !== endpoint.vlanrange);
      return { ...endpoint, vlan: endpoint.vlanrange, port: { ...endpoint.port, usedVlans } };
    }),
  );

  protected isMyPort(port: { customer?: { customerId: string } | null }) {
    return port.customer?.customerId === this.auth.viewingCustomer().customerId;
  }

  protected validVlan(port: { usedVlans: string[] }, vlanString: string, currentVlan: string) {
    return vlanString === currentVlan || isValidVlan(port, vlanString);
  }

  protected vlanMatches() {
    const vlans = this.endpoints().map((endpoint) => endpoint.vlan);
    const hasMultiVlan = vlans.some((v) => /\D/.test(v));
    return !hasMultiVlan || new Set(vlans).size === 1;
  }

  private findMyCircuit() {
    const myPorts = this.circuits()[0].endpoints.map((e) => e.port?.subscriptionId);
    return this.circuits().findIndex((circuit) =>
      myPorts.every((p) => circuit.endpoints.some((e) => e.port?.subscriptionId === p)),
    );
  }

  private ports() {
    return this.endpoints().map((endpoint) => ({
      vlan: endpoint.vlan === "" ? endpoint.vlanrange : endpoint.vlan,
      subscription_id: endpoint.port.subscriptionId ?? "",
    }));
  }

  private buildLightpathCommand() {
    const modify = new ModifyLightPath();
    modify.subscription = this.subscription();
    modify.updateCircuit(0, this.ports());
    return { modify, action: SelfServiceCommandKeys.ModifyLightpath };
  }

  private buildRedundantLightpathCommand() {
    const modify = new ModifyRedundantLightPath();
    modify.subscription = this.subscription();
    modify.updateCircuit(this.findMyCircuit(), this.ports());
    return { modify, action: SelfServiceCommandKeys.ModifyRedundantLightpath };
  }

  protected readonly buildCommand = () => {
    const { modify, action } =
      this.isRedundant() ? this.buildRedundantLightpathCommand() : this.buildLightpathCommand();
    const command = new SelfServiceCommand(this.subscription().subscriptionId, action);
    command.payload = modify;
    return { command, action };
  };
}
